// Camada de query de relatórios e exports (`spec/17-frontend.md` §8.1, WEB-08).
//
// CONTRATO DE QUERY KEY (`app/live-events.ts`): domínio primeiro, `mode` depois. As duas
// keys daqui começam por `['reports','summary']` e `['reports','exports']` porque é por
// esse prefixo que o mapa de invalidação de §4.5 as alcança, `report_export.ready` e
// `report_export.failed` invalidam a lista de exports.
//
// Nenhum tipo de resposta escrito à mão (`spec/17` §5.2): tudo sai de `types/public.d.ts`.

import { queryOptions } from '@tanstack/react-query';
import type { Mode } from '../../app/mode.tsx';
import { api } from '../client.ts';
import { isApiError } from '../errors.ts';
import type { paths } from '../types/public.d.ts';

/** Corpo `200` de um `GET` do documento gerado. */
type Get200<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer T } } } };
}
  ? T
  : never;

export type ReportSummary = Get200<'/v1/me/reports/summary'>;
export type ExportPage = Get200<'/v1/me/reports/exports'>;
export type ReportExport = ExportPage['data'][number];
export type ExportKind = ReportExport['kind'];
export type ExportStatus = ReportExport['status'];

export type CreateExportBody =
  paths['/v1/me/reports/exports']['post']['requestBody']['content']['application/json'];
export type ExportAccepted =
  paths['/v1/me/reports/exports']['post']['responses'][202]['content']['application/json'];

export const SUMMARY_KEY = ['reports', 'summary'] as const;
export const EXPORTS_KEY = ['reports', 'exports'] as const;

export interface IsoRange {
  /** `YYYY-MM-DD` em `America/Sao_Paulo` (`spec/13` §4.1). */
  from: string;
  to: string;
}

/**
 * Teto do período na API (`resolvePeriod` de `@la-mafia/core`, `MAX_PERIOD_DAYS`). A
 * contagem é a mesma de lá, inclusiva nas duas pontas, para o cliente recusar
 * exatamente o que o servidor recusaria, nunca um dia a mais ou a menos.
 */
export const MAX_PERIOD_DAYS = 366;

const DAY_MS = 86_400_000;

/** Meio-dia UTC: `YYYY-MM-DD` cru é meia-noite UTC e o horário de verão comeria um dia. */
function dayMs(isoDate: string): number {
  return Date.parse(`${isoDate}T12:00:00Z`);
}

/** Dias do período, contando as duas pontas (`2026-01-01`→`2026-01-01` é 1). */
export function periodDays(range: IsoRange): number {
  return Math.round((dayMs(range.to) - dayMs(range.from)) / DAY_MS) + 1;
}

/** `true` = a API devolveria `400 validation_failed`; a tela nem chama (item 2 da task). */
export function isPeriodTooLong(range: IsoRange): boolean {
  const days = periodDays(range);
  return Number.isFinite(days) && days > MAX_PERIOD_DAYS;
}

/**
 * `platform_summary_csv` fica de fora: é export de admin e o seller leva
 * `403 insufficient_role` (`modules/reports/exports-routes.ts`). Botão morto é pior que
 * botão ausente.
 */
export const SELLER_EXPORT_KINDS = [
  'transactions_csv',
  'withdrawals_csv',
  'ledger_csv',
] as const satisfies readonly ExportKind[];

export type SellerExportKind = (typeof SELLER_EXPORT_KINDS)[number];

export const EXPORT_KIND_LABEL: Record<ExportKind, string> = {
  transactions_csv: 'Transações',
  withdrawals_csv: 'Saques',
  ledger_csv: 'Extrato',
  platform_summary_csv: 'Resumo da plataforma',
};

export function isSellerExportKind(value: string): value is SellerExportKind {
  return (SELLER_EXPORT_KINDS as readonly string[]).includes(value);
}

/** Fallback obrigatório de §4.5 enquanto o SSE não está alcançável. */
const POLL_MS = 30_000;

/** Export `queued`/`running` não tem evento até terminar: polling curto (item 3 da task). */
const PENDING_POLL_MS = 4_000;

export function isPendingExport(item: ReportExport): boolean {
  return item.status === 'queued' || item.status === 'running';
}

export function pollIntervalFor(page: ExportPage | undefined): number {
  return page?.data.some(isPendingExport) === true ? PENDING_POLL_MS : POLL_MS;
}

/**
 * Sempre `from`/`to`, nunca `period`: mandar os dois é `400 validation_failed`, e o
 * `DateRangeFilter` já resolve todo preset em um par de datas.
 */
export function summaryQueryOptions(mode: Mode, range: IsoRange) {
  return queryOptions({
    queryKey: [...SUMMARY_KEY, mode, range.from, range.to] as const,
    queryFn: () =>
      api<ReportSummary>('/v1/me/reports/summary', {
        mode,
        query: { from: range.from, to: range.to },
      }),
    // Período acima do teto não vira request: o 400 seria certo e a mensagem do servidor
    // é em inglês (`period must not exceed 366 days`).
    enabled: !isPeriodTooLong(range),
    refetchInterval: POLL_MS,
  });
}

/**
 * A lista não é paginada na tela: `limit` 20 (o default da API) é mais export do que um
 * seller pede em uma hora, o teto é 5/h. Sem `useInfiniteQuery`, sem "carregar mais".
 */
export const EXPORTS_LIMIT = 20;

export function exportsQueryOptions(mode: Mode) {
  return queryOptions({
    queryKey: [...EXPORTS_KEY, mode] as const,
    queryFn: () =>
      api<ExportPage>('/v1/me/reports/exports', { mode, query: { limit: EXPORTS_LIMIT } }),
    refetchInterval: ({ state }) => pollIntervalFor(state.data),
  });
}

export function requestExport(mode: Mode, body: CreateExportBody): Promise<ExportAccepted> {
  return api<ExportAccepted>('/v1/me/reports/exports', { method: 'POST', mode, body });
}

/**
 * `download_url` só existe no GET do detalhe e só em `ready` dentro da validade
 * (`spec/13` §4.6): a listagem nunca traz a URL assinada, então o botão de download
 * busca o export na hora do clique.
 */
export function fetchExport(mode: Mode, id: string): Promise<ReportExport> {
  return api<ReportExport>(`/v1/me/reports/exports/${id}`, { mode });
}

/**
 * `429 rate_limited` em minutos (item 4 da task). `Retry-After` só chega ao navegador
 * quando a API o expõe no CORS (ver "Questões abertas"): sem ele sobra a mensagem do
 * servidor, e por isso o retorno é `undefined` em vez de um número inventado.
 */
export function retryAfterMinutes(error: unknown): number | undefined {
  if (!isApiError(error) || error.retryAfterS === undefined) {
    return undefined;
  }
  return Math.max(1, Math.ceil(error.retryAfterS / 60));
}

export function isRateLimited(error: unknown): boolean {
  return isApiError(error) && error.code === 'rate_limited';
}
